/* eslint-disable no-magic-numbers */
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { MaterialModule } from 'src/app/modules/material.module';
import { WorkOverviewEntry } from 'src/app/work-and-rest-user/enums/work-overview-entry.enum';
import { CellColoringStrategy } from 'src/app/work-and-rest-user/models/cell-coloring-strategy.type';
import {
  IUserWorkOverviewRow,
  IUnitComplianceCell,
} from 'src/app/work-and-rest-user/models/work-overview-details.model';
import { WorkOverviewColoringService } from 'src/app/work-and-rest-user/services/work-overview-coloring.service';
import { AdditionalTimeApprovalStatus } from 'src/app/work-and-rest-user/enums/additional-time-approval.status.enum';
import { TenantUserPrivilegesService } from 'src/app/tenant-selector/services/tenant-user-privileges.service';
import { Privileges } from 'src/app/core/privileges/privileges';
import { TimeDisplayPipe } from 'src/app/core/pipes/time-display.pipe';
import { NumberRoundPipe } from 'src/app/core/pipes/number-round.pipe';
import { UIElementsModule } from '../../ui-elements/ui-elements.module';

@Component({
  selector: 'app-work-overview-table',
  standalone: true,
  imports: [
    CommonModule,
    MaterialModule,
    TranslateModule,
    UIElementsModule,
    TimeDisplayPipe,
    NumberRoundPipe,
  ],
  templateUrl: './work-overview-table.component.html',
  styleUrl: './work-overview-table.component.scss',
})
export class WorkOverviewTableComponent implements OnChanges {
  @Input() toDate: Date | null = null;
  @Input() plannedEndDate: Date | undefined = undefined;
  @Input() embeddedView = false;
  @Input() showHourHeader = false;
  @Input() coloringStrategy: CellColoringStrategy = 'classicManual';

  @Input() set daySchedules(value: IUserWorkOverviewRow[]) {
    this.daySchedulesData = value;
    this.updateFilteredDaySchedules();
  }
  get daySchedules(): IUserWorkOverviewRow[] {
    return this.filteredDaySchedules;
  }

  @Output() readonly editClicked = new EventEmitter<string>();
  @Output() readonly deleteClicked = new EventEmitter<string>();

  canUpdateAfterAppoval: boolean;
  canDeleteAfterAppoval: boolean;
  canUpdate: boolean;
  canDelete: boolean;

  UserScheduleEntry = WorkOverviewEntry;
  AdditionalTimeApprovalStatus = AdditionalTimeApprovalStatus;
  WORK_OVERVIEW_ENTRY_KEY: Record<number, string> = {
    [WorkOverviewEntry.Work]: 'work',
    [WorkOverviewEntry.Rest]: 'rest',
    [WorkOverviewEntry.AdditionalWork]: 'manualWork',
    [WorkOverviewEntry.AdditionalRest]: 'manualRest',
    [WorkOverviewEntry.None]: '',
  };

  private daySchedulesData: IUserWorkOverviewRow[] = [];
  private filteredDaySchedules: IUserWorkOverviewRow[] = [];

  HALF_HOURS_IN_A_DAY = 48;
  TWO_DIGIT_NUMBER_START_INDEX = 20;

  hourHeaderLabels = Array.from({ length: this.HALF_HOURS_IN_A_DAY / 2 }, (_, i) => {
    if (i >= this.TWO_DIGIT_NUMBER_START_INDEX / 2) {
      return `${i}`;
    }

    return `0${i}`;
  });

  hoursInADay = Array.from({ length: this.HALF_HOURS_IN_A_DAY }, (_, i) => {
    if (i % 2 === 0) {
      if (i >= this.TWO_DIGIT_NUMBER_START_INDEX) {
        return `${i / 2}`;
      }
      return `0${i / 2}`;
    }
    return '';
  });

  halfHoursInADay = Array.from({ length: this.HALF_HOURS_IN_A_DAY }, (_, i) => {
    let label = '';
    if (i >= this.TWO_DIGIT_NUMBER_START_INDEX) {
      label = `${i / 2}`;
    } else {
      label = `0${i / 2}`;
    }

    if (label.endsWith('.5')) {
      label = label.slice(0, -2);
      label = `${label}:30`;
    } else {
      label = `${label}:00`;
    }

    return label;
  });

  constructor(
    private tenantUserPrivileges: TenantUserPrivilegesService,
    private coloringService: WorkOverviewColoringService
  ) {
    this.canUpdateAfterAppoval = this.tenantUserPrivileges.userHasPrivilege(
      Privileges.UpdateAfterApproval
    );
    this.canDeleteAfterAppoval = this.tenantUserPrivileges.userHasPrivilege(
      Privileges.DeleteAfterApproval
    );
    this.canUpdate = this.tenantUserPrivileges.userHasPrivilege(Privileges.AdditionalTimeUpdate);
    this.canDelete = this.tenantUserPrivileges.userHasPrivilege(Privileges.AdditionalTimeDelete);
  }

  ngOnChanges(): void {
    this.updateFilteredDaySchedules();
  }

  onRowClick(daySchedule: IUserWorkOverviewRow): void {
    if (daySchedule.showDetails !== undefined) {
      daySchedule.showDetails = !daySchedule.showDetails;
    }
  }

  edit(id: string): void {
    this.editClicked.emit(id);
  }

  remove(id: string): void {
    this.deleteClicked.emit(id);
  }

  isCellCutOff(daySchedule: IUserWorkOverviewRow, cellIndex: number): boolean {
    if (!this.toDate || !daySchedule.date) {
      return false;
    }

    const rowDate = new Date(daySchedule.date);
    const toDate = new Date(this.toDate);

    // Set both dates to start of day for comparison
    const rowDateNormalized = new Date(rowDate);
    const toDateNormalized = new Date(toDate);
    rowDateNormalized.setHours(0, 0, 0, 0);
    toDateNormalized.setHours(0, 0, 0, 0);

    // If the row date is after the toDate, all cells should be cut off
    if (rowDateNormalized > toDateNormalized) {
      return true;
    }

    // If the row date is before the toDate, no cells should be cut off
    if (rowDateNormalized < toDateNormalized) {
      return false;
    }

    // If the row date equals the toDate, calculate which cells should be cut off
    // Each cell represents 30 minutes, so cellIndex 0 = 00:00, cellIndex 1 = 00:30, etc.
    const cellTimeInMinutes = cellIndex * 30;
    const toDateTimeInMinutes = toDate.getHours() * 60 + toDate.getMinutes();

    return cellTimeInMinutes >= toDateTimeInMinutes;
  }

  onCellClick(event: MouseEvent): void {
    event.stopPropagation();
  }

  getCellClasses(
    schedule: IUnitComplianceCell,
    daySchedule: IUserWorkOverviewRow,
    scheduleIndex: number
  ): Record<string, boolean> {
    const displayType = this.coloringService.getDisplayCellType({
      cellType: schedule?.unitComplianceColor ?? WorkOverviewEntry.None,
      coloringStrategy: this.coloringStrategy,
      rowUnitCompliances: daySchedule.unitCompliances,
      scheduleIndex,
    });

    const hasPlanned = !!schedule?.hasPlannedBeingOnBoardThisCell;
    const isFirstInPlannedSequence =
      hasPlanned && !daySchedule.unitCompliances[scheduleIndex - 1]?.hasPlannedBeingOnBoardThisCell;
    const isLastInPlannedSequence =
      hasPlanned && !daySchedule.unitCompliances[scheduleIndex + 1]?.hasPlannedBeingOnBoardThisCell;

    return {
      // Structural/Layout classes
      'grid-cell': !this.embeddedView,
      'grid-cell-embedded-view': this.embeddedView,
      'grid-cell-border': !!schedule,

      // Work Type classes
      work: displayType === WorkOverviewEntry.Work,
      rest: displayType === WorkOverviewEntry.Rest,
      'additional-work': displayType === WorkOverviewEntry.AdditionalWork,
      'additional-rest': displayType === WorkOverviewEntry.AdditionalRest,

      // Status & Validation classes
      'violation-of-rules': !!schedule?.isBreach,
      'cut-off': !!schedule?.isCutOff,

      // Activity Sequence classes
      'has-planned-activity': hasPlanned,
      'start-of-planned-activity': isFirstInPlannedSequence,
      'end-of-planned-activity': isLastInPlannedSequence,
    };
  }

  private updateFilteredDaySchedules(): void {
    this.filteredDaySchedules = this.daySchedulesData
      .filter((daySchedule) => this.isRowVisible(daySchedule))
      .map((daySchedule) => ({
        ...daySchedule,
        unitCompliances: this.processUnitCompliances(daySchedule),
      }));
  }

  private processUnitCompliances(daySchedule: IUserWorkOverviewRow): IUnitComplianceCell[] {
    if (!daySchedule.unitCompliances) {
      return [];
    }
    return daySchedule.unitCompliances.map((cell, index) => ({
      ...cell,
      isCutOff: this.isCellCutOff(daySchedule, index),
    }));
  }

  private isRowVisible(daySchedule: IUserWorkOverviewRow): boolean {
    if (!daySchedule.date) {
      return true;
    }
    const rowDate = new Date(daySchedule.date);
    rowDate.setHours(0, 0, 0, 0);

    if (this.plannedEndDate && this.toDate) {
      const plannedEnd = new Date(this.plannedEndDate);
      const toDate = new Date(this.toDate);
      plannedEnd.setHours(0, 0, 0, 0);
      toDate.setHours(0, 0, 0, 0);

      // If toDate is AFTER plannedEndDate, show all days up to toDate
      if (toDate > plannedEnd) {
        return rowDate <= toDate;
      }
      // If toDate is BEFORE or EQUAL to plannedEndDate, hide days after plannedEndDate
      return rowDate <= plannedEnd;
    }
    return true;
  }
}
